import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  getTractorServiceStatus,
  isTractorAvailableForWindow,
  isBlockingOosStatus,
  type TractorServiceStatus,
  type ServiceRecordLike,
} from "@/lib/tractor-service-status";

/**
 * Fetches every active (open/updated) tractor_service_records row and
 * derives a per-tractor OOS status via the client-side mirror of
 * `public.tractor_service_status`. Returned map only contains tractors that
 * have at least one active service record. Callers that don't find their
 * tractor id should treat it as `available`.
 */
export function useTractorOosStatusMap(opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled ?? true;

  const query = useQuery({
    queryKey: ["tractor-oos-status-map"],
    enabled,
    // Service records change infrequently; refresh every 60s.
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tractor_service_records" as never)
        .select("tractor_id, status, window_start, window_end")
        .in("status", ["open", "updated"]);
      if (error) throw error;

      type Row = ServiceRecordLike & { tractor_id: string | null };
      const rows = (data ?? []) as unknown as Row[];

      const byTractor = new Map<string, ServiceRecordLike[]>();
      for (const r of rows) {
        if (!r.tractor_id) continue;
        const arr = byTractor.get(r.tractor_id) ?? [];
        arr.push(r);
        byTractor.set(r.tractor_id, arr);
      }

      const now = new Date();
      const status = new Map<string, TractorServiceStatus>();
      const records = new Map<string, ServiceRecordLike[]>();
      byTractor.forEach((recs, id) => {
        const s = getTractorServiceStatus(recs, now);
        if (s !== "available") status.set(id, s);
        records.set(id, recs);
      });
      return { status, records };
    },
  });

  return {
    statusById: query.data?.status ?? new Map<string, TractorServiceStatus>(),
    recordsById: query.data?.records ?? new Map<string, ServiceRecordLike[]>(),
    isLoading: query.isLoading,
    isEnabled: enabled,
  };
}

/**
 * Derive the subset of tractor ids that should be treated as blocked for a
 * specific booking window (start..end). Uses `isTractorAvailableForWindow`.
 * Returns an empty set when the flag is off.
 */
export function computeOosBlockedIdsForWindow(
  recordsById: Map<string, ServiceRecordLike[]>,
  start: Date | string | null | undefined,
  end: Date | string | null | undefined,
): Set<string> {
  const blocked = new Set<string>();
  const rawS = start ? new Date(start) : null;
  const rawE = end ? new Date(end) : null;
  const sValid = rawS && !Number.isNaN(rawS.getTime()) ? rawS : null;
  const eValid = rawE && !Number.isNaN(rawE.getTime()) ? rawE : null;
  if (!sValid && !eValid) {
    // No usable form window — fall back to current-status blocking.
    recordsById.forEach((recs, id) => {
      const st = getTractorServiceStatus(recs);
      if (isBlockingOosStatus(st)) blocked.add(id);
    });
    return blocked;
  }
  // Treat a partial window as a point-in-time overlap check against the
  // known side. This still correctly blocks trucks whose active service
  // window (including future/indefinite) covers that instant, instead of
  // silently reverting to the "right now" fallback.
  const s = sValid ?? eValid!;
  const e = eValid ?? sValid!;
  recordsById.forEach((recs, id) => {
    if (!isTractorAvailableForWindow(recs, s, e)) blocked.add(id);
  });
  return blocked;
}